/**
 * Exponential backoff with full jitter, capped, and abort-aware.
 *
 * Backoff formula (full-jitter — Polly / AWS guidance):
 *   delay = random(0, min(cap, base * 2^attempt))
 *
 * Defaults: base = 250ms, cap = 4s, max 3 attempts. The api client caller
 * can tune per-request.
 */

export interface RetryConfig {
  /** Total attempts including the first. Default 3. */
  attempts: number;
  /** Base backoff in ms. Default 250. */
  baseMs: number;
  /** Maximum backoff in ms. Default 4000. */
  capMs: number;
}

export const DEFAULT_RETRY: RetryConfig = {
  attempts: 3,
  baseMs: 250,
  capMs: 4_000,
};

export const fullJitter = (attemptIndex: number, base: number, cap: number): number => {
  const expo = Math.min(cap, base * 2 ** attemptIndex);
  return Math.floor(Math.random() * expo);
};

export const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = (): void => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const cleanup = (): void => {
      clearTimeout(t);
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
